import { useCallback, useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { ApiError, fetchLesson, reportProgress, saveNote, setCompleted, type LessonView } from '../api';
import { useTenant } from '../app';
import { CheckIcon, Kicker, Link, PlayIcon, Thumb } from '../components';
import { formatClock, pad2 } from '../format';

interface Props {
  slug: string;
  number: number;
  onClose: () => void;
  onNavigate: (number: number) => void;
  /** Nach Fortschrittsänderungen: Kursübersicht neu laden. */
  onProgress: () => void;
}

const PROGRESS_MS = 10_000;
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

/**
 * Die Lektion als Fenster über der Kursübersicht (Konzept 4.4): Player, Titel, Text, Materialien,
 * Sprungmarken, Notizen, erledigt-Knopf, vor und zurück. Auf dem Handy füllt das Fenster den Bildschirm.
 */
export function LessonOverlay({ slug, number, onClose, onNavigate, onProgress }: Props) {
  const tenant = useTenant();
  const [view, setView] = useState<LessonView | null | undefined>(undefined);
  const [error, setError] = useState<'locked' | 'not_found' | null>(null);
  const [seekTo, setSeekTo] = useState<{ seconds: number; nonce: number } | null>(null);
  const [takenOver, setTakenOver] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setView(undefined);
    setError(null);
    setTakenOver(false);
    fetchLesson(slug, number)
      .then((v) => {
        setView(v);
        document.title = `${pad2(v.lesson.number)} ${v.lesson.title} – ${v.course.title.replace('\n', ' ')} – ${tenant.name}`;
        panelRef.current?.scrollTo({ top: 0 });
      })
      .catch((e: unknown) => {
        setView(null);
        setError(e instanceof ApiError && e.status === 403 ? 'locked' : 'not_found');
      });
  }, [slug, number, tenant.name]);

  // Hintergrund nicht scrollen, Escape schließt.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const markCompleted = useCallback(
    async (completed: boolean) => {
      if (!view) return;
      setView({ ...view, progress: { ...view.progress, completed } });
      await setCompleted(view.lesson.id, completed).catch(() => undefined);
      onProgress();
    },
    [view, onProgress],
  );

  const onAutoCompleted = useCallback(() => {
    setView((v) => (v ? { ...v, progress: { ...v.progress, completed: true } } : v));
    onProgress();
  }, [onProgress]);

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="overlay__panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={view ? `Lektion ${pad2(view.lesson.number)}` : 'Lektion'}>
        <button type="button" className="overlay__close" onClick={onClose} aria-label="Schließen">
          ×
        </button>

        {view === undefined && !error && <div className="overlay__loading" />}

        {error && (
          <div className="overlay__message">
            <h2>{error === 'locked' ? 'Diese Lektion ist noch gesperrt' : 'Diese Lektion gibt es nicht'}</h2>
            <p className="muted">{error === 'locked' ? 'Sie wird mit dem Kauf oder zum angegebenen Datum freigeschaltet.' : 'Vielleicht wurde sie verschoben.'}</p>
            <button type="button" className="button button--secondary" onClick={onClose}>
              Zurück zur Übersicht
            </button>
          </div>
        )}

        {view && (
          <>
            {takenOver ? (
              <div className="overlay__message">
                <h2>Du schaust gerade auf einem anderen Gerät</h2>
                <p className="muted">Dein Zugang gilt für {tenant.name === '' ? 'wenige' : 'eine begrenzte Zahl'} Geräte gleichzeitig. Dort läuft es weiter – oder du machst hier weiter.</p>
                <button type="button" className="button" onClick={() => window.location.reload()}>
                  Hier weiterschauen
                </button>
              </div>
            ) : (
              <Player view={view} seekTo={seekTo} onTakenOver={() => setTakenOver(true)} onAutoCompleted={onAutoCompleted} />
            )}

            <div className="lesson">
              <div className="lesson__main">
                <Kicker>
                  Lektion {pad2(view.lesson.number)}
                  {view.lesson.durationSeconds ? ` · ${formatClock(view.lesson.durationSeconds)}` : ''}
                </Kicker>
                <h2 className="lesson__title">{view.lesson.title}</h2>
                {view.lesson.speakers && <p className="lesson__speakers accent">{view.lesson.speakers}</p>}
                {view.lesson.body && <div className="lesson__body">{view.lesson.body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</div>}

                <div className="lesson__actions">
                  <button type="button" className={`button ${view.progress.completed ? 'button--done' : 'button--secondary'}`} onClick={() => void markCompleted(!view.progress.completed)}>
                    <CheckIcon /> {view.progress.completed ? 'Erledigt' : 'Als erledigt markieren'}
                  </button>
                  {view.lesson.materials.length > 0 && (
                    <a href="#lektion-materialien" className="button button--ghost">
                      Materialien ({view.lesson.materials.length})
                    </a>
                  )}
                </div>

                {view.lesson.materials.length > 0 && (
                  <section className="lesson__materials" id="lektion-materialien">
                    <Kicker>Materialien zu dieser Lektion</Kicker>
                    <ul className="materials materials--compact">
                      {view.lesson.materials.map((m) => (
                        <li key={m.id}>
                          <a href={m.url} target={m.kind === 'link' ? '_blank' : undefined} rel="noopener" className="material">
                            <span className="material__kind">{m.kind === 'file' ? 'PDF' : 'Link'}</span>
                            <span className="material__body">
                              <strong>{m.title}</strong>
                              {m.description && <span className="muted small">{m.description}</span>}
                            </span>
                            <span className="material__action">{m.kind === 'file' ? 'Herunterladen' : 'Öffnen ↗'}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <Notes lessonId={view.lesson.id} initial={view.note} />
              </div>

              <aside className="lesson__side">
                <Kicker>Sprungmarken aus dem Workbook</Kicker>
                {view.lesson.chapters.length === 0 ? (
                  <p className="muted small">Zu dieser Lektion gibt es keine Learnings im Workbook.</p>
                ) : (
                  <ol className="chapters">
                    {view.lesson.chapters.map((c) => (
                      <li key={c.offsetSeconds}>
                        <button type="button" className="chapter" onClick={() => setSeekTo({ seconds: c.offsetSeconds, nonce: Date.now() })} disabled={!view.lesson.video}>
                          <span className="chapter__time">{formatClock(c.offsetSeconds)}</span>
                          <span className="chapter__label">{c.label}</span>
                          <PlayIcon className="chapter__play" />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </aside>
            </div>

            <nav className="lesson__nav">
              {view.prev ? (
                <button type="button" className="lesson__navlink" onClick={() => onNavigate(view.prev!.number)}>
                  <span className="muted small">← Zurück</span>
                  <span>
                    {pad2(view.prev.number)} · {view.prev.shortTitle ?? view.prev.title}
                  </span>
                </button>
              ) : (
                <span />
              )}
              {view.next ? (
                <button type="button" className="lesson__navlink lesson__navlink--next" onClick={() => onNavigate(view.next!.number)}>
                  <span className="muted small">Weiter →</span>
                  <span>
                    {pad2(view.next.number)} · {view.next.shortTitle ?? view.next.title}
                  </span>
                </button>
              ) : (
                <Link href={`/kurs/${slug}`} className="lesson__navlink lesson__navlink--next">
                  <span className="muted small">Letzte Lektion</span>
                  <span>Zurück zur Übersicht</span>
                </Link>
              )}
            </nav>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Der Player: HLS über hls.js (Safari kann es von Haus aus), startet an der gemerkten Stelle, meldet alle
 * zehn Sekunden die Position, bietet Geschwindigkeit, folgt Sprungmarken. Native Bedienleiste – die kennt
 * jeder, und auf dem iPhone ist sie die zuverlässigste.
 */
function Player({ view, seekTo, onTakenOver, onAutoCompleted }: { view: LessonView; seekTo: { seconds: number; nonce: number } | null; onTakenOver: () => void; onAutoCompleted: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [started, setStarted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const completedRef = useRef(view.progress.completed);
  const lastSentRef = useRef(-1);
  const video = view.lesson.video;

  // Video laden, sobald der Kunde gestartet hat (iOS spielt mit Ton nicht von selbst – der Klick zählt).
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !video || !started) return;
    let hls: Hls | null = null;
    let cancelled = false;
    const startAt = view.progress.completed ? 0 : view.progress.positionSeconds;
    const begin = () => {
      if (startAt > 0 && startAt < (view.lesson.durationSeconds ?? Infinity) - 5) el.currentTime = startAt;
      void el.play().catch(() => undefined);
    };
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = video.hlsUrl;
      el.addEventListener('loadedmetadata', begin, { once: true });
    } else {
      void import('hls.js').then(({ default: HlsLib }) => {
        if (cancelled || !HlsLib.isSupported()) return;
        hls = new HlsLib({ startPosition: startAt > 0 ? startAt : -1, capLevelToPlayerSize: true, maxBufferLength: 30, maxMaxBufferLength: 90, backBufferLength: 30 });
        hlsRef.current = hls;
        hls.loadSource(video.hlsUrl);
        hls.attachMedia(el);
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => void el.play().catch(() => undefined));
        hls.on(HlsLib.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === HlsLib.ErrorTypes.NETWORK_ERROR) hls?.startLoad();
          else if (data.type === HlsLib.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
          else {
            hls?.destroy();
            hlsRef.current = null;
          }
        });
      });
    }
    return () => {
      cancelled = true;
      hls?.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.hlsUrl, started]);

  // Sprungmarke: anspringen und abspielen; ohne Start zuerst starten.
  useEffect(() => {
    if (!seekTo) return;
    if (!started) {
      setStarted(true);
      const el = videoRef.current;
      if (el) el.addEventListener('loadedmetadata', () => (el.currentTime = seekTo.seconds), { once: true });
      return;
    }
    const el = videoRef.current;
    if (el) {
      el.currentTime = seekTo.seconds;
      void el.play().catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekTo?.nonce]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, started]);

  // Fortschritt melden: alle zehn Sekunden, beim Pausieren und am Ende.
  const send = useCallback(
    async (playing: boolean) => {
      const el = videoRef.current;
      if (!el) return;
      const position = Math.floor(el.currentTime);
      if (playing && position === lastSentRef.current) return;
      lastSentRef.current = position;
      try {
        const r = await reportProgress(view.lesson.id, position, playing);
        if (r.takenOver) {
          el.pause();
          onTakenOver();
          return;
        }
        if (r.completed && !completedRef.current) {
          completedRef.current = true;
          onAutoCompleted();
        }
      } catch {
        /* nächste Meldung versucht es wieder */
      }
    },
    [view.lesson.id, onTakenOver, onAutoCompleted],
  );

  useEffect(() => {
    if (!started) return;
    const timer = setInterval(() => {
      const el = videoRef.current;
      if (el && !el.paused && !el.ended) void send(true);
    }, PROGRESS_MS);
    return () => clearInterval(timer);
  }, [started, send]);

  return (
    <div className="player">
      {!video ? (
        <Thumb src={null} seed={view.lesson.number} className="player__placeholder">
          <div className="player__badge">
            <span className="player__big">{pad2(view.lesson.number)}</span>
            <span>{view.lesson.videoStatus === 'processing' ? 'Das Video wird gerade verarbeitet – gleich geht es los.' : view.lesson.videoStatus === 'failed' ? 'Das Video konnte nicht verarbeitet werden.' : 'Zu dieser Lektion gibt es kein Video.'}</span>
          </div>
        </Thumb>
      ) : (
        <>
          <video
            ref={videoRef}
            className="player__video"
            poster={video.posterUrl}
            playsInline
            controls={started}
            controlsList="nodownload"
            preload="metadata"
            onPause={() => void send(false)}
            onEnded={() => void send(false)}
            onContextMenu={(e) => e.preventDefault()}
          />
          {!started && (
            <button type="button" className="player__start" onClick={() => setStarted(true)} aria-label="Abspielen">
              <span className="player__start-badge">
                <span className="player__big">{pad2(view.lesson.number)}</span>
                <span className="player__start-title">{view.lesson.shortTitle ?? view.lesson.title}</span>
                {view.progress.positionSeconds > 0 && !view.progress.completed && <span className="player__resume">Weiter ab {formatClock(view.progress.positionSeconds)}</span>}
              </span>
              <span className="player__start-icon">
                <PlayIcon />
              </span>
            </button>
          )}
          {started && (
            <div className="player__speeds" role="group" aria-label="Geschwindigkeit">
              {SPEEDS.map((s) => (
                <button key={s} type="button" className={`speed${speed === s ? ' speed--active' : ''}`} onClick={() => setSpeed(s)}>
                  {s.toString().replace('.', ',')}×
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Notes({ lessonId, initial }: { lessonId: string; initial: string }) {
  const [body, setBody] = useState(initial);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setBody(initial);
    setState('idle');
  }, [lessonId, initial]);

  function change(value: string) {
    setBody(value);
    setState('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveNote(lessonId, value)
        .then(() => setState('saved'))
        .catch(() => setState('idle'));
    }, 800);
  }

  return (
    <section className="notes">
      <div className="notes__head">
        <Kicker>Meine Notizen</Kicker>
        <span className="muted small">{state === 'saving' ? 'wird gespeichert …' : state === 'saved' ? 'gespeichert' : 'nur für dich sichtbar'}</span>
      </div>
      <textarea className="notes__field" rows={4} value={body} onChange={(e) => change(e.target.value)} placeholder="Was willst du dir aus dieser Lektion merken?" />
    </section>
  );
}
